/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  Dispatch,
  SyntheticEvent,
  useCallback,
  useReducer,
  useEffect,
  useRef,
} from 'react';
import useGetOtpBankAcctTypeQid from '../common/hooks/useGetOtpBankAcctTypeQid';
import useGetAutopayInfo from '../common/hooks/useGetAutopayInfo';
import useGetPaymentInfo from '../common/hooks/useGetPaymentInfo';
import useGetEventDate from '../common/hooks/useGetEventDate';
import useGetLoanBalances from '../common/hooks/useGetLoanBalances';
import useAbortController from '../common/hooks/useAbortController';
import axiosCall from '../services/axios';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTimestampAsDate, isStrEmptyOrNull, isValidRoutingNumber } from '../common/UtilityFunctions';
import useGetMnthlyImp from '../common/hooks/useGetMnthlyImp';
import useGetServicingPayment from '../common/hooks/useGetServicingPayment';
import { checkRoutingInfo, modelAccessInfo } from '../common/queryFunctions';
import { useLoanContext } from './LoanContext';
import { useAuthContext } from './AuthContext';
import useQidList from '../common/hooks/useQidList';
import { useCommunicationContext } from './CommunicationContext';
import { numericFormatter } from 'react-number-format';
import { useBrwrContext } from './BorrowerContext';
import useGetServicingInstallments from '../common/hooks/useGetServicingInstallments';

type reduceAutopayState = {
  otpRoutingNo: string;
  otpBankAccountNo: string;
  otpBankName: string;
  otpBankAccountTypeKeyword: string;
};

type reducePaymentState = {
  dueDate: null;
  apparPi: number;
  totalEscrowShortage: number;
  escrowShortageMonthlyRepaymentAmt: number;
  escrowShortageRepayMonths: number;
  lateChrgAcrd: number;
};

type reduceLoanBalancesState = {
  suspenseBal: number;
  assessmentBal: number;
  corpAdvBal: number;
  lcAcrdBal: number;
};

type reduceOtpState = {
  additionalPrincipal: number;
  additionalEscrow: number;
  lateCharges: number;
  corporateAdvance: number;
  assessments: number;
  suspense: number;
  processingFee: number;
  processDate: null;
  payment: number;
};

type statusAlertType = {
  status: string;
  message: string;
};

type qidType = {
  keyword: string;
  description: string;
};

type routingInfoType = {
  valid: boolean;
  description: string;
};

type batchTransactionResponseType = {
  endpoint: string;
  data: {
    timestamp: number;
    batchNo: string;
    cnt: string;
  };
  error: string;
};

type bankingDetailsType = {
  otpRoutingNo: string;
  otpBankAccountNo: string;
  otpBankName: string;
  otpBankAccountTypeKeyword: string;
};

type otpValue = {
  autopayInfo: reduceAutopayState;
  dispatchAutopayInfo: Dispatch<{}>;
  paymentInfo: reducePaymentState;
  loanBalancesInfo: reduceLoanBalancesState;
  otpInfo: reduceOtpState;
  dispatchOtpInfo: Dispatch<{}>;
  handleSubmit: (e: SyntheticEvent) => void;
  handleRemoveTransaction: (e: SyntheticEvent, transaction) => void;
  handleSaveTransaction: (e: SyntheticEvent, transaction) => void;
  status: string;
  statusAlert: statusAlertType;
  setStatusAlert: (statusAlertType) => void;
  otpBankAcctTypeQidInfo: Array<qidType>;
  otpRoutingNoError: boolean;
  setOtpRoutingNoError: (boolean) => void;
  otpBankNameError: boolean;
  setOtpBankNameError: (boolean) => void;
  otpBankAccountNoError: boolean;
  setOtpBankAccountNoError: (boolean) => void;
  otpBankAccountTypeError: boolean;
  setOtpBankAccountTypeError: (boolean) => void;
  additionalPrincipalError: boolean;
  setAdditionalPrincipalError: (boolean) => void;
  additionalEscrowError: boolean;
  setAdditionalEscrowError: (boolean) => void;
  lateChargesError: boolean;
  setLateChargesError: (boolean) => void;
  lateChargesMaxError: boolean;
  setLateChargesMaxError: (boolean) => void;
  corporateAdvanceError: boolean;
  setCorporateAdvanceError: (boolean) => void;
  corporateAdvanceMaxError: boolean;
  setCorporateAdvanceMaxError: (boolean) => void;
  assessmentsError: boolean;
  setAssessmentsError: (boolean) => void;
  assessmentsMaxError: boolean;
  setAssessmentsMaxError: (boolean) => void;
  suspenseError: boolean;
  setSuspenseError: (boolean) => void;
  suspenseMaxError: boolean;
  setSuspenseMaxError: (boolean) => void;
  processingFeeError: boolean;
  setProcessingFeeError: (boolean) => void;
  processDateError: boolean;
  setProcessDateError: (boolean) => void;
  processTomorrowError: boolean;
  setProcessTomorrowError: (boolean) => void;
  paymentError: boolean;
  setPaymentError: (boolean) => void;
  totalError: boolean;
  setTotalError: (boolean) => void;
  retainInfo: boolean;
  setRetainInfo: (boolean) => void;
  removePayment: boolean;
  setRemovePayment: (booleann) => void;
  tempPayment: number;
  setTempPayment: (number) => void;
  removeProcessingFee: boolean;
  setRemoveProcessingFee: (boolean) => void;
  oneTimePaymentModalOpen: boolean;
  setOneTimePaymentModalOpen: (boolean) => void;
  oneTimePaymentModalContent: string;
  setOneTimePaymentModalContent: (string) => void;
  deleteRow: any;
  setDeleteRow: (any) => void;
  validateFields: (SyntheticEvent) => void;
  isValidatingPayment: boolean;
  loadingMessage: string;
  isCurrent: boolean;
  currentMonthDueWithoutPayment: boolean;
  escrowShortageModalOpen: boolean;
  setEscrowShortageModalOpen: (boolean) => void;
  handleMonthlyInstallments: () => void;
  handleLumpSum: () => void;
  choseLumpSum: boolean;
  pageError: boolean;
  futurePayments: any[];
  hasFuturePayment: boolean;
  hasAutopay: boolean;
  stopCodeST01: boolean;
  stopCodeST02: boolean;
  stopCodeST03: boolean;
  stopCodeST04: boolean;
  stopCodeST20: boolean;
  hasDisablingStopCode: boolean;
  handleRemoveStopCode: (stopCode: string) => void;
  statusForeclosure: boolean;
  statusDeedInLieu: boolean;
  statusShortSale: boolean;
  statusServicingTransfer: boolean;
  hasDisablingStatus: boolean;
  minProcessDate: () => Dayjs;
  totalWithoutFee: number;
  routingError: boolean;
  routingErrorMessage: string;
  serviceManager: boolean;
  handleRemoveBankDetails: () => void;
  originalBankingDetails: bankingDetailsType | null;
};

const OneTimePaymentContext = createContext<otpValue | null>(null);

const useOneTimePaymentContext = () => {
  const oneTimePaymentContext = useContext(OneTimePaymentContext);
  if (oneTimePaymentContext === undefined) {
    // throwing this error helps in development when the context is out of scope.
    throw new Error('oneTimePaymentContext must be inside a OneTimePaymentProvider');
  }
  return oneTimePaymentContext;
};

type oneTimePaymentProviderProps = {
  children: ReactNode;
};

function reducer(state, action) {
  switch (action.type) {
    case 'changed_otpInfo': {
      return {
        ...action.otpInfo,
      };
    }
    case 'changed_property': {
      return {
        ...state,
        ...action.property,
      };
    }
  }
  throw Error('Unkown action: ' + action.type);
}

const OneTimePaymentProvider = ({ children }: oneTimePaymentProviderProps): JSX.Element => {
  const {
    loanNumber,
    loanFha,
    lpi,
    servicingStopCodes,
    servStatus,
    originationType,
    propertyState,
    bk,
    refetchLoanData,
  } = useLoanContext();
  const { get } = useAbortController();
  const { firstName, lastName, servicerUser, token, userId } = useAuthContext();
  const { otpBankAcctTypeQidInfo, otpBankAcctTypeQidStatus } = useGetOtpBankAcctTypeQid();
  const { autopayInfo, autopayStatus, dispatchAutopayInfo, refetchAutopayInfo } = useGetAutopayInfo(loanNumber);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const nextPayment = useMemo(() => {
    const nextPaymentDate = dayjs.unix(lpi).tz('America/Los_Angeles').add(1, 'month');
    if (nextPaymentDate.isValid()) return nextPaymentDate.format('YYYYMMDD');
    return ''; // date is not valid
  }, [lpi]);
  const { servInstData, reloadServInstData } = useGetServicingInstallments(loanNumber, nextPayment);
  const { paymentInfo, paymentStatus } = useGetPaymentInfo(loanNumber, nextPayment);
  const { mnthlyImpInfo, mnthlyImpStatus } = useGetMnthlyImp(loanNumber, nextPayment);
  const { eventDateInfo, eventDateStatus } = useGetEventDate(loanNumber, 'AEA', '00');
  const { servicingLoanPayments, servicingErrorMessages, loadingServicing, refetch } =
    useGetServicingPayment(loanNumber);
  const { loanBalancesInfo, loanBalancesStatus } = useGetLoanBalances(
    loanNumber,
    dayjs().tz('America/Los_Angeles').format('YYYYMMDD'),
  );
  const [otpInfo, dispatchOtpInfo] = useReducer(reducer, {
    additionalPrincipal: 0,
    additionalEscrow: 0,
    lateCharges: 0,
    processDate: null,
    corporateAdvance: 0,
    assessments: 0,
    suspense: 0,
    processingFee: 0,
    payment: 0,
  });
  const [removePayment, setRemovePayment] = useState(false);
  const [tempPayment, setTempPayment] = useState(0);
  const [removeProcessingFee, setRemoveProcessingFee] = useState(false);

  const [submitStatus, setSubmitStatus] = useState('done');
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });

  const [retainInfo, setRetainInfo] = useState(false);
  const [otpRoutingNoError, setOtpRoutingNoError] = useState(false);
  const [otpBankNameError, setOtpBankNameError] = useState(false);
  const [otpBankAccountNoError, setOtpBankAccountNoError] = useState(false);
  const [otpBankAccountTypeError, setOtpBankAccountTypeError] = useState(false);
  const [additionalPrincipalError, setAdditionalPrincipalError] = useState(false);
  const [additionalEscrowError, setAdditionalEscrowError] = useState(false);
  const [lateChargesError, setLateChargesError] = useState(false);
  const [lateChargesMaxError, setLateChargesMaxError] = useState(false);
  const [corporateAdvanceError, setCorporateAdvanceError] = useState(false);
  const [corporateAdvanceMaxError, setCorporateAdvanceMaxError] = useState(false);
  const [assessmentsError, setAssessmentsError] = useState(false);
  const [assessmentsMaxError, setAssessmentsMaxError] = useState(false);
  const [suspenseError, setSuspenseError] = useState(false);
  const [suspenseMaxError, setSuspenseMaxError] = useState(false);
  const [processingFeeError, setProcessingFeeError] = useState(false);
  const [processDateError, setProcessDateError] = useState(false);
  const [processTomorrowError, setProcessTomorrowError] = useState(false);
  const [paymentError, setPaymentError] = useState(false);
  const [totalError, setTotalError] = useState(false);
  const [oneTimePaymentModalOpen, setOneTimePaymentModalOpen] = useState(false);
  const [oneTimePaymentModalContent, setOneTimePaymentModalContent] = useState('');
  const [deleteRow, setDeleteRow] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [escrowShortageModalOpen, setEscrowShortageModalOpen] = useState(false);
  const [choseLumpSum, setChoseLumpSum] = useState(false);
  const [pageError, setPageError] = useState(false);
  const [routingError, setRoutingError] = useState(false);
  const [routingErrorMessage, setRoutingErrorMessage] = useState('');
  const [serviceManager, setServiceManager] = useState(false);
  const { isCommenting, createComment } = useCommunicationContext();
  const { loadingQid: loadingCategories, qid: categories } = useQidList('CMT4_CD.027');
  const actionCodesQidListResponse = useQidList('CMT4_CD.025');
  const actionCodes = useMemo(() => actionCodesQidListResponse.qid, [actionCodesQidListResponse]);
  const resultCodesQidListResponse = useQidList('CMT4_CD.026');
  const resultCodes = useMemo(() => resultCodesQidListResponse.qid, [resultCodesQidListResponse]);
  const [confirmationNumbers, setConfirmationNumbers] = useState([]);
  const [originalBankingDetails, setOriginalBankingDetails] = useState<bankingDetailsType | null>(null);
  const { borrowerDataArr } = useBrwrContext();
  const [commentData, setCommentData] = useState({
    userUid: userId,
    userName: ((firstName || '') + ' ' + (lastName || '')).trim(),
    addedByUid: userId,
    addedByUserName: ((firstName || '') + ' ' + (lastName || '')).trim(), // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: 'XX',
    actionCodeDescription: 'XX -',
    actionCodeKeyword: 'XX',
    codes: '',
    commentCategory: 'C',
    commentCategoryDescription: 'C - Collections Comment',
    commentCategoryKeyword: 'C',
    loanNumbers: `${loanNumber}`,
    loanNumber: `${loanNumber}`,
    text: '',
    promiseToPay: ``,
    promiseToPayAmount: '',
    resultCode: 'XX',
    resultCodeDescription: 'XX -',
    resultCodeKeyword: 'XX',
    success: '',
  });
  const [commentErrorData, setCommentErrorData] = useState({
    userUid: '',
    userName: '',
    addedByUserName: '', // 05/19/2026 - SQC-639, Justin Han-Nguyen - added this field to store the original commenter
    actionCode: '',
    actionCodeDescription: '',
    actionCodeKeyword: '',
    codes: '',
    commentCategory: '',
    commentCategoryDescription: '',
    commentCategoryKeyword: '',
    loanNumbers: '',
    loanNumber: '',
    text: '',
    promiseToPay: '',
    promiseToPayAmount: '',
    resultCode: '',
    resultCodeDescription: '',
    resultCodeKeyword: '',
    generic: '',
  });

  // Tracks whether One Time Payment validation is currently in progress
  const isValidatingPaymentRef = useRef(false);

  // Controls the Submit button while payment validation is in progress
  const [isValidatingPayment, setIsValidatingPayment] = useState(false);

  useEffect(() => {
    dispatchOtpInfo({
      type: 'changed_property',
      property: { payment: servInstData.borrowerPiti?.toFixed(2) },
    });
  }, [servInstData]);

  useEffect(() => {
    if (eventDateInfo != null && !eventDateInfo?.date) {
      setEscrowShortageModalOpen(false);
    } else {
      // Require an actual shortage with an outstanding monthly repayment before displaying the escrow shortage options
      const hasShortage = paymentInfo?.totalEscrowShortage < 0 && paymentInfo?.escrowShortageMonthlyRepaymentAmt > 0;
      setEscrowShortageModalOpen(hasShortage);
    }
  }, [paymentInfo, eventDateInfo]);

  const isCurrent = useMemo(() => {
    return dayjs().tz('America/Los_Angeles').diff(dayjs.unix(lpi).tz('America/Los_Angeles'), 'month') <= 1;
  }, [lpi]);

  const totalWithoutFee = useMemo(() => {
    return Number(
      (
        (removePayment ? tempPayment : Number(otpInfo.payment)) +
        otpInfo.additionalPrincipal +
        otpInfo.additionalEscrow +
        otpInfo.lateCharges +
        otpInfo.corporateAdvance +
        otpInfo.assessments +
        otpInfo.suspense
      ).toFixed(2),
    );
  }, [otpInfo, removePayment, tempPayment]);

  useEffect(() => {
    if (!originationType || !propertyState || !otpInfo) return;
    const noFeeStates = ['CA', 'CO', 'IN', 'KS', 'MD', 'PA'];
    if (!servicerUser || !isCurrent) {
      dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
    } else {
      if (originationType.charAt(0) !== 'A' && originationType.charAt(0) !== 'B') {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else if (noFeeStates.includes(propertyState)) {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else if (bk) {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else if (otpInfo.suspense < 0 && totalWithoutFee === 0) {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else if (loanFha) {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else if (removeProcessingFee) {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 0 } });
      } else {
        dispatchOtpInfo({ type: 'changed_property', property: { processingFee: 7.5 } });
      }
    }
  }, [
    paymentInfo,
    otpInfo,
    propertyState,
    bk,
    originationType,
    servicerUser,
    isCurrent,
    totalWithoutFee,
    removeProcessingFee,
  ]);

  const futurePayments = useMemo(() => {
    if (!servicingLoanPayments) return [];

    return servicingLoanPayments.filter((payment) => {
      return (
        payment.active &&
        !payment.posted &&
        (dayjs.unix(payment.dateReceived).tz('America/Los_Angeles').isSame(dayjs().tz('America/Los_Angeles'), 'day') ||
          dayjs.unix(payment.dateReceived).tz('America/Los_Angeles').isAfter(dayjs().tz('America/Los_Angeles')))
      );
    });
  }, [servicingLoanPayments]);

  const hasFuturePayment = useMemo(() => {
    if (!servicingLoanPayments) return false;
    return (
      servicingLoanPayments.filter(
        (payment) =>
          dayjs.unix(payment.dateReceived).isSame(dayjs().tz('America/Los_Angeles')) ||
          dayjs.unix(payment.dateReceived).isAfter(dayjs().tz('America/Los_Angeles')),
      ).length > 0
    );
  }, [servicingLoanPayments]);

  const hasAutopay = useMemo(() => {
    return autopayInfo.autoPayStatusKeyword === 'YES' || autopayInfo.autoPayStatusKeyword === 'PREPAY';
  }, [autopayInfo]);

  // Add all active stop codes to a set for quick lookup
  const stopCodeSet = useMemo(
    () => new Set(servicingStopCodes?.map((stopCode) => stopCode.servStopCodeKeyword)),
    [servicingStopCodes],
  );
  const stopCodeST01 = stopCodeSet.has('ST01');
  const stopCodeST02 = stopCodeSet.has('ST02');
  const stopCodeST03 = stopCodeSet.has('ST03');
  const stopCodeST04 = stopCodeSet.has('ST04');
  const stopCodeST20 = stopCodeSet.has('ST20');
  const hasDisablingStopCode = useMemo(() => stopCodeST02 || stopCodeST03 || stopCodeST20, [servicingStopCodes]);

  // Remove a given stop code from the loan
  const handleRemoveStopCode = useCallback(
    async (stopCode: string) => {
      if (!loanNumber || !token) return;

      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };

      const newStopCodeList = servicingStopCodes.map((sc) =>
        sc.servStopCodeKeyword === stopCode ? { ...sc, servStopCode: '' } : sc,
      );
      console.log(newStopCodeList);
      const requestBody = {
        servicingStopCodes: newStopCodeList,
      };
      try {
        const response = await axiosCall(
          {
            url: `live/loan/${loanNumber}`,
            method: 'post',
            data: requestBody,
          },
          config,
          headers,
        );
        if (!response || response?.error) {
          throw Error('Unable to delete stop code.' + response?.error);
        }
        setStatusAlert({
          status: 'success',
          message: `Stop Code was removed successfully: ${stopCode}`,
        });
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'error',
          message: `Error: ${e}`,
        });
      }
      setOneTimePaymentModalOpen(false);
      refetchLoanData();
    },
    [servicingStopCodes],
  );

  // Set the modal content based on active stop codes
  useEffect(() => {
    if (stopCodeST02) {
      setOneTimePaymentModalContent('stopCodeST02');
      setOneTimePaymentModalOpen(true);
    } else if (stopCodeST03) {
      setOneTimePaymentModalContent('stopCodeST03');
      setOneTimePaymentModalOpen(true);
    } else if (stopCodeST20) {
      setOneTimePaymentModalContent('stopCodeST20');
      setOneTimePaymentModalOpen(true);
    }
  }, [servicingStopCodes]);

  /* Foreclosure Status Codes: 
      - Referred to Foreclosure
      - 1st Legal Completed
      - Foreclosure - Sale Scheduled
      - Foreclosure - Sale Held - Back to Beneficiary
      - Foreclosure - Sale Held - Third Party Sale
  */
  const FORECLOSURE_STATUSES = ['FCR', 'FCL', 'FSS', 'FSB', 'FST'];
  const statusForeclosure = FORECLOSURE_STATUSES.includes(servStatus);

  /*
    Deed-in-Lieu Status Codes:
      - Deed-in-Lieu Referred
      - Deed-in-Lieu Completed
  */
  const DEED_IN_LIEU_STATUSES = ['DIL', 'DLC'];
  const statusDeedInLieu = DEED_IN_LIEU_STATUSES.includes(servStatus);

  /*
    Short Sale Status Codes:
      - Short Sale Referred
      - Short Sale Completed
  */
  const SHORT_SALE_STATUSES = ['SSR', 'SSC'];
  const statusShortSale = SHORT_SALE_STATUSES.includes(servStatus);

  /*
    Servicing Transfer Status Codes:
      - Servicing Transfer
  */
  const SERVICING_TRANSFER_STATUSES = ['SXR'];
  const statusServicingTransfer = SERVICING_TRANSFER_STATUSES.includes(servStatus);

  const hasDisablingStatus = useMemo(
    () => statusForeclosure || statusDeedInLieu || statusShortSale || statusServicingTransfer,
    [servStatus],
  );

  // Set the modal content based on the loan's servStatus
  useEffect(() => {
    if (statusForeclosure) {
      setOneTimePaymentModalContent('statusForeclosure');
      setOneTimePaymentModalOpen(true);
    } else if (statusDeedInLieu) {
      setOneTimePaymentModalContent('statusDeedInLieu');
      setOneTimePaymentModalOpen(true);
    } else if (statusShortSale) {
      setOneTimePaymentModalContent('statusShortSale');
      setOneTimePaymentModalOpen(true);
    } else if (statusServicingTransfer) {
      setOneTimePaymentModalContent('statusServicingTransfer');
      setOneTimePaymentModalOpen(true);
    }
  }, [servStatus]);

  const isCurrentMonthPaid = useMemo(() => {
    return dayjs().tz('America/Los_Angeles').diff(dayjs.unix(lpi).tz('America/Los_Angeles'), 'month') <= 1;
  }, [lpi]);

  // The current month payment is due, but the user is not paying the mortgage payment
  const currentMonthDueWithoutPayment = useMemo(() => {
    return (
      removePayment && dayjs().tz('America/Los_Angeles').diff(dayjs.unix(lpi).tz('America/Los_Angeles'), 'month') === 1
    );
  }, [lpi, removePayment]);

  useEffect(() => {
    if (!isCurrent || currentMonthDueWithoutPayment) {
      dispatchOtpInfo({
        type: 'changed_property',
        property: { additionalPrincipal: 0 },
      });
    }
  }, [isCurrent, currentMonthDueWithoutPayment]);

  const status = useMemo(() => {
    const statuses = [
      otpBankAcctTypeQidStatus,
      autopayStatus,
      paymentStatus,
      submitStatus,
      eventDateStatus,
      mnthlyImpStatus,
      loanBalancesStatus,
    ];
    if (statuses.includes('error') || servicingErrorMessages) {
      setPageError(true);
      setStatusAlert({
        status: 'error',
        message: 'Unable to retrieve one time payment details.',
      });
      return 'error';
    }
    if (statuses.includes('loading') || loadingServicing) {
      return 'loading';
    }
    setPageError(false);
    return 'done';
  }, [
    otpBankAcctTypeQidStatus,
    autopayStatus,
    paymentStatus,
    submitStatus,
    eventDateStatus,
    mnthlyImpStatus,
    loadingServicing,
    servicingErrorMessages,
    loanBalancesStatus,
  ]);

  //any payment after 10pm should be considered the next day
  const minProcessDate = useCallback(() => {
    const today = dayjs().tz('America/Los_Angeles');
    return today.hour() >= 22 ? today.startOf('day').add(1, 'day') : today.startOf('day');
  }, []);

  const handleMonthlyInstallments = useCallback(() => {
    setEscrowShortageModalOpen(false);
    dispatchOtpInfo({
      type: 'changed_property',
      property: {
        payment: paymentInfo.apparPi + mnthlyImpInfo.mnthlyImpAmt + paymentInfo.escrowShortageMonthlyRepaymentAmt,
      },
    });
  }, [paymentInfo, mnthlyImpInfo]);

  const handleLumpSum = useCallback(() => {
    setEscrowShortageModalOpen(false);
    setChoseLumpSum(true);

    // Convert the negative shortage balance to the positive amount being collected
    dispatchOtpInfo({
      type: 'changed_property',
      property: {
        additionalEscrow: paymentInfo.totalEscrowShortage < 0 ? Math.abs(paymentInfo.totalEscrowShortage) : 0,
      },
    });
  }, [paymentInfo]);

  // Keep track of the original banking details while the user edits the form
  useEffect(() => {
    if (
      originalBankingDetails === null &&
      autopayInfo !== null &&
      autopayInfo.otpRoutingNo !== '' &&
      autopayInfo.otpBankAccountNo !== '' &&
      autopayInfo.otpBankName !== '' &&
      autopayInfo.otpBankAccountTypeKeyword !== ''
    ) {
      setOriginalBankingDetails({
        otpRoutingNo: autopayInfo.otpRoutingNo,
        otpBankAccountNo: autopayInfo.otpBankAccountNo,
        otpBankName: autopayInfo.otpBankName,
        otpBankAccountTypeKeyword: autopayInfo.otpBankAccountTypeKeyword,
      });
    }
  }, [autopayInfo, originalBankingDetails]);

  const validateFields = useCallback(
    async (e: SyntheticEvent) => {
      e.preventDefault();

      // Ignore additional submit attempts while the current validation is still running
      if (isValidatingPaymentRef.current) return;

      isValidatingPaymentRef.current = true;
      setIsValidatingPayment(true);

      try {
        let isError = false;
        if (!isValidRoutingNumber(autopayInfo.otpRoutingNo)) {
          setOtpRoutingNoError(true);
          isError = true;
        } else {
          setOtpRoutingNoError(false);
        }
        if (isStrEmptyOrNull(autopayInfo.otpBankName)) {
          setOtpBankNameError(true);
          isError = true;
        } else {
          setOtpBankNameError(false);
        }
        if (isStrEmptyOrNull(autopayInfo.otpBankAccountNo.replace(/\s/g, ''))) {
          setOtpBankAccountNoError(true);
          isError = true;
        } else {
          setOtpBankAccountNoError(false);
        }
        if (!autopayInfo.otpBankAccountTypeKeyword) {
          setOtpBankAccountTypeError(true);
          isError = true;
        } else {
          setOtpBankAccountTypeError(false);
        }
        if ((!otpInfo.additionalPrincipal && otpInfo.additionalPrincipal !== 0) || isNaN(otpInfo.additionalPrincipal)) {
          setAdditionalPrincipalError(true);
          isError = true;
        } else {
          setAdditionalPrincipalError(false);
        }
        if ((!otpInfo.additionalEscrow && otpInfo.additionalEscrow !== 0) || isNaN(otpInfo.additionalEscrow)) {
          setAdditionalEscrowError(true);
          isError = true;
        } else {
          setAdditionalEscrowError(false);
        }
        if (
          !otpInfo.processDate ||
          dayjs().tz('America/Los_Angeles').isAfter(dayjs.unix(otpInfo.processDate).tz('America/Los_Angeles'), 'day')
        ) {
          setProcessDateError(true);
          isError = true;
        } else if (
          minProcessDate().isSame(dayjs().tz('America/Los_Angeles').startOf('day').add(1, 'day'), 'day') &&
          dayjs().tz('America/Los_Angeles').isSame(dayjs.unix(otpInfo.processDate).tz('America/Los_Angeles'), 'day')
        ) {
          setProcessTomorrowError(true);
          isError = true;
        } else {
          setProcessTomorrowError(false);
          setProcessDateError(false);
        }
        if ((!otpInfo.lateCharges && otpInfo.lateCharges !== 0) || isNaN(otpInfo.lateCharges)) {
          setLateChargesError(true);
          isError = true;
        } else if (otpInfo.lateCharges > loanBalancesInfo.lcAcrdBal) {
          setLateChargesMaxError(true);
          isError = true;
        } else {
          setLateChargesError(false);
          setLateChargesMaxError(false);
        }
        if ((!otpInfo.corporateAdvance && otpInfo.corporateAdvance !== 0) || isNaN(otpInfo.corporateAdvance)) {
          setCorporateAdvanceError(true);
          isError = true;
        } else if (otpInfo.corporateAdvance > -1 * loanBalancesInfo.corpAdvBal) {
          setCorporateAdvanceMaxError(true);
          isError = true;
        } else {
          setCorporateAdvanceError(false);
          setCorporateAdvanceMaxError(false);
        }
        if ((!otpInfo.assessments && otpInfo.assessments !== 0) || isNaN(otpInfo.assessments)) {
          setAssessmentsError(true);
          isError = true;
        } else if (otpInfo.assessments > loanBalancesInfo.assessmentBal) {
          setAssessmentsMaxError(true);
          isError = true;
        } else {
          setAssessmentsMaxError(false);
          setAssessmentsError(false);
        }
        if ((!otpInfo.suspense && otpInfo.suspense !== 0) || isNaN(otpInfo.suspense)) {
          setSuspenseError(true);
          isError = true;
        } else if (-1 * otpInfo.suspense > loanBalancesInfo.suspenseBal) {
          setSuspenseMaxError(true);
          isError = true;
        } else {
          setSuspenseError(false);
          setSuspenseMaxError(false);
        }
        if ((!otpInfo.processingFee && otpInfo.processingFee !== 0) || isNaN(otpInfo.processingFee)) {
          setProcessingFeeError(true);
          isError = true;
        } else {
          setProcessingFeeError(false);
        }
        if (totalWithoutFee < 0 || (otpInfo.suspense >= 0 && totalWithoutFee === 0)) {
          setTotalError(true);
          isError = true;
        } else {
          setTotalError(false);
        }
        if (!otpRoutingNoError && !otpBankAccountNoError) {
          try {
            const routingInfoData = await checkRoutingInfo(
              loanNumber,
              autopayInfo.otpRoutingNo,
              autopayInfo.otpBankAccountNo,
              token,
            );
            if (routingInfoData === null || !routingInfoData.valid) {
              const modelInfoData = await modelAccessInfo('SERMG', servicerUser, token);
              setServiceManager(!modelInfoData ? false : modelInfoData.fullAccess);
              setRoutingError(true);
              setRoutingErrorMessage(
                routingInfoData !== null ? routingInfoData.description : 'Invalid information entered',
              );

              isError = true;
            } else {
              setRoutingError(false);
            }
          } catch (error) {
            // Treat routing validation request failures as validation errors and prevent payment submission
            console.error('Unable to validate routing information:', error);
            setRoutingError(true);
            setRoutingErrorMessage('Unable to validate routing information.');
            isError = true;
          }
        }
        if (isError) {
          return;
        } else {
          //No Errors, Show Confirmation Modal
          setOneTimePaymentModalOpen(true);
          setOneTimePaymentModalContent('create');
        }
      } finally {
        // Release the validation lock after validation completes or exits early.
        isValidatingPaymentRef.current = false;
        setIsValidatingPayment(false);
      }
    },
    [
      otpInfo.additionalEscrow,
      otpInfo.additionalPrincipal,
      autopayInfo.otpBankAccountNo,
      autopayInfo.otpBankAccountTypeKeyword,
      autopayInfo.otpBankName,
      autopayInfo.otpRoutingNo,
      otpInfo.processDate,
      otpInfo.lateCharges,
      otpInfo.corporateAdvance,
      otpInfo.assessments,
      otpInfo.suspense,
      otpInfo.processingFee,
      loanBalancesInfo.assessmentBal,
      loanBalancesInfo.corpAdvBal,
      loanBalancesInfo.suspenseBal,
      loanBalancesInfo.lcAcrdBal,
      minProcessDate,
      totalWithoutFee,
    ],
  );

  const handleSaveTransaction = useCallback(
    async (e: SyntheticEvent, transaction) => {
      if (!token) return;
      let transactionResponse = null;
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      let requestBody = {};
      const originalProcessDate = futurePayments.find(
        (p) => p.batchNo === transaction.batchNo && p.cnt === transaction.cnt,
      )?.dateReceived;
      switch (transaction.mcdTransType) {
        case '11':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            prinCurtailReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        case '13':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            extraEscrowReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        case '14':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            lateChargeReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        case '26':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            corpAdvanceReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        case '20':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            assessmentsReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        case '16':
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            suspenseReqstd: transaction.amount,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
          break;
        default:
          requestBody = {
            dateReceived: transaction.dateReceived,
            amount: transaction.amount,
            prinCurtailReqstd: transaction.prinCurtailReqstd,
            extraEscrowReqstd: transaction.extraEscrowReqstd,
            lateChargeReqstd: transaction.lateChargeReqstd,
            corpAdvanceReqstd: transaction.corpAdvanceReqstd,
            assessmentsReqstd: transaction.assessmentsReqstd,
            suspenseReqstd: transaction.suspenseReqstd,
            routingNo: transaction.routingNo,
            accountNo: transaction.accountNo,
            accountTypeKeyword: transaction.accountTypeKeyword,
          };
      }
      try {
        const response = await axiosCall(
          {
            url: `live/servicing/payments/batch/${transaction.batchNo}/cnt/${transaction.cnt}`,
            method: 'post',
            data: requestBody,
          },
          config,
          headers,
        );
        if (!response || response?.error) {
          throw Error('Unable to save OTP transaction. ' + response?.error);
        }
        setStatusAlert({
          status: 'success',
          message: 'Payment Saved!',
        });
        refetch();
        transactionResponse = response;
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'error',
          message: `Failed to save payment details: ${e}`,
        });
      }
      handleEditTransactionComment(e, transactionResponse, originalProcessDate);
      handleEditCancelTransactionEmail(transactionResponse, originalProcessDate, true);
    },
    [token, get, futurePayments],
  );

  // Add a comment to the loan when any change is made to a saved transaction
  const handleEditTransactionComment = useCallback(
    async (e, transactionResponse, originalProcessDate) => {
      if (!token || !loanNumber) return;

      try {
        let comment = {
          ...commentData,
          text:
            `The payment originally scheduled for ${getTimestampAsDate(originalProcessDate)} has been updated as of ${new Date(
              transactionResponse.timestamp * 1000,
            ).toLocaleTimeString('en-US', {
              timeZone: 'America/Los_Angeles',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}. Please see the new details below:\n\n` +
            `Process Date: ${getTimestampAsDate(transactionResponse.dateReceived)}\n` +
            `Payment Amount: ${numericFormatter(transactionResponse.amount.toString(), {
              thousandSeparator: true,
              prefix: '$',
              decimalScale: 2,
              fixedDecimalScale: true,
            })}\n` +
            `Confirmation Number: ${transactionResponse.batchNo.toString() + '-' + transactionResponse.cnt.toString()}`,
        };
        await createComment(
          e,
          comment,
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          false,
        );
      } catch (e) {
        console.error(e);
      }
    },
    [
      token,
      loanNumber,
      commentData,
      createComment,
      actionCodes,
      categories,
      resultCodes,
      setCommentErrorData,
      setCommentData,
    ],
  );

  const handleEditCancelTransactionEmail = useCallback(
    async (transactionResponse, originalProcessDate, isEdit) => {
      if (!token || !loanNumber) return;

      try {
        const emailType = isEdit ? 'Updated' : 'Cancelled';
        const controller = get();
        const config = { signal: controller.signal };
        const requestBody = {
          to: borrowerDataArr
            .filter((b) => b.borrowerOnNote && b.borrowerType !== 'R')
            .map((b) => b.emailAddress)
            .join(','),
          headerMap: {
            LOAN: loanNumber,
          },
          bodyMap: {
            PMT_DATE: getTimestampAsDate(originalProcessDate),
            ACTIONED: emailType.toLowerCase(),
            TIME_OF_EDIT: new Date(transactionResponse.timestamp * 1000).toLocaleTimeString('en-US', {
              timeZone: 'America/Los_Angeles',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            }),
            PMT_STATUS: emailType,
            PMT_SCH_DATE: isEdit ? getTimestampAsDate(transactionResponse.dateReceived) : 'N/A',
            PMT_AMT: numericFormatter(transactionResponse.amount.toString(), {
              thousandSeparator: true,
              decimalScale: 2,
              fixedDecimalScale: true,
            }),
            CONFIRMATION_NO: transactionResponse.batchNo.toString() + '-' + transactionResponse.cnt.toString(),
          },
          isHtmlMsg: true,
          preview: false,
        };

        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };
        await axiosCall(
          { url: `live/email-template/RCS OTP Edit Confirmation/send`, method: 'post', data: requestBody },
          config,
          headers,
        );
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'warning',
          message:
            'Payment modified - Unable to send edit/cancellation email, please validate borrower email address(es).',
        });
      }
    },
    [borrowerDataArr, loanNumber, otpInfo, totalWithoutFee, token, get],
  );

  const handleConfirmationEmail = useCallback(
    async (transactionResponse) => {
      if (!token || !loanNumber) return;
      const controller = get();
      const config = { signal: controller.signal };
      const requestBody = {
        to: borrowerDataArr
          .filter((b) => b.borrowerOnNote && b.borrowerType !== 'R')
          .map((b) => b.emailAddress)
          .join(','),
        bodyMap: {
          PMT_DATE: getTimestampAsDate(transactionResponse.timestamp),
          TIME: new Date(transactionResponse.timestamp * 1000).toLocaleTimeString('en-US', {
            timeZone: 'America/Los_Angeles',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }),
          LOAN: loanNumber,
          ACCNT_LAST_FOUR: autopayInfo?.otpBankAccountNo.slice(-4),
          PMT_AMT: numericFormatter(otpInfo.payment.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          ADDL_PRIN: numericFormatter(otpInfo.additionalPrincipal.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          ADDL_ESC: numericFormatter(otpInfo.additionalEscrow.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          ESC_SHORTAGE: numericFormatter(paymentInfo?.escrowShortageMonthlyRepaymentAmt.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          LATE_CHARGES: numericFormatter(otpInfo.lateCharges.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          CORP_ADV: numericFormatter(otpInfo.corporateAdvance.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          ASSESSMENTS: numericFormatter(otpInfo.assessments.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          PROC_FEE: numericFormatter(otpInfo.processingFee.toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          TOTAL_PMT: numericFormatter((totalWithoutFee + otpInfo.processingFee).toString(), {
            thousandSeparator: true,
            decimalScale: 2,
            fixedDecimalScale: true,
          }),
          PROC_DATE: getTimestampAsDate(otpInfo.processDate),
          CONFIRMATION_NO: transactionResponse.batchNo.toString() + '-' + transactionResponse.cnt.toString(),
        },
        isHtmlMsg: true,
        preview: false,
      };
      try {
        const headers = {
          'Session-Token': token,
          'Content-Type': 'application/json',
        };
        await axiosCall(
          { url: `live/email-template/RCS OTP Confirmation/send`, method: 'post', data: requestBody },
          config,
          headers,
        );
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'warning',
          message: 'Payment submitted - Unable to send confirmation email, please validate borrower email address(es).',
        });
      }
    },
    [loanNumber, get, token, borrowerDataArr, totalWithoutFee, otpInfo, autopayInfo, paymentInfo],
  );

  const handleDeleteTransactionComment = useCallback(
    async (e, transactionResponse, originalProcessDate) => {
      if (!token || !loanNumber) return;

      try {
        let comment = {
          ...commentData,
          text: `The payment scheduled for ${getTimestampAsDate(originalProcessDate)} with amount of ${numericFormatter(
            transactionResponse.amount.toString(),
            {
              thousandSeparator: true,
              decimalScale: 2,
              fixedDecimalScale: true,
            },
          )} has been cancelled as of ${getTimestampAsDate(transactionResponse.timestamp)} ${new Date(
            transactionResponse.timestamp * 1000,
          ).toLocaleTimeString('en-US', {
            timeZone: 'America/Los_Angeles',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}.\n\n Confirmation Number: ${transactionResponse.batchNo.toString() + '-' + transactionResponse.cnt.toString()}`,
        };
        await createComment(
          e,
          comment,
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          false,
        );
      } catch (error) {
        console.error(`Error: ${error.message}`);
      }
    },
    [
      token,
      loanNumber,
      commentData,
      createComment,
      actionCodes,
      categories,
      resultCodes,
      setCommentErrorData,
      setCommentData,
    ],
  );

  const handleRemoveTransaction = useCallback(
    async (e: SyntheticEvent, transaction) => {
      if (!token) return;
      const controller = get();
      const config = { signal: controller.signal };
      let transactionResponse = null;
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const requestBody = {
        active: false,
      };
      try {
        const response = await axiosCall(
          {
            url: `live/servicing/payments/batch/${transaction.batchNo}/cnt/${transaction.cnt}`,
            method: 'post',
            data: requestBody,
          },
          config,
          headers,
        );
        if (!response || response?.error) {
          throw Error('Unable to delete OTP transaction. ' + response?.error);
        }
        refetch();
        transactionResponse = response;
      } catch (e) {
        console.error(e);
        return;
      }
      setOneTimePaymentModalOpen(false);
      handleEditCancelTransactionEmail(transactionResponse, transaction.dateReceived, false);
      await handleDeleteTransactionComment(e, transactionResponse, transaction.dateReceived);
    },
    [token, get, refetch, handleEditCancelTransactionEmail, handleDeleteTransactionComment],
  );

  const handleAddComment = useCallback(
    async (e: SyntheticEvent, confirmationNos?: string[]) => {
      if (!token || !loanNumber) return;

      try {
        let comment = { ...commentData };
        if (servicerUser) {
          comment = {
            ...commentData,
            text: `One Time Payment: ${autopayInfo?.otpBankName}-${autopayInfo?.otpBankAccountNo}, Amount: ${numericFormatter(
              totalWithoutFee.toString(),
              { thousandSeparator: true, prefix: '$', decimalScale: 2, fixedDecimalScale: true },
            )} Process On: ${dayjs.unix(otpInfo.processDate).format('MM/DD/YYYY')} Processed By: ${firstName} ${lastName}\n
            Confirmation Number(s): ${confirmationNos.map((c) => ' #' + c)}
            ${stopCodeST01 ? '\nStop Code: ST01' : ''}
            ${stopCodeST04 ? '\nStop Code: ST04' : ''}`,
          };
        } else {
          comment = {
            ...commentData,
            text: `One Time Payment Confirmation email sent to borrower (Payment Date: ${dayjs.unix(otpInfo.processDate).format('MM/DD/YYYY')}, Amount: ${numericFormatter(
              totalWithoutFee.toString(),
              { thousandSeparator: true, prefix: '$', decimalScale: 2, fixedDecimalScale: true },
            )})
            Confirmation Number(s): ${confirmationNos.map((c) => ' #' + c)}
            ${stopCodeST01 ? '\nStop Code: ST01' : ''}
            ${stopCodeST04 ? '\nStop Code: ST04' : ''}`,
          };
        }
        await createComment(
          e,
          comment,
          actionCodes,
          categories,
          resultCodes,
          setCommentErrorData,
          setCommentData,
          false,
        );
      } catch (error) {
        console.error(`Error: ${error.message}`);
      }
    },
    [
      commentData,
      actionCodes,
      categories,
      resultCodes,
      setCommentErrorData,
      setCommentData,
      loanNumber,
      token,
      createComment,
      confirmationNumbers,
      servicerUser,
      autopayInfo,
      totalWithoutFee,
      otpInfo,
      firstName,
      lastName,
    ],
  );

  const handleSubmit = useCallback(
    async (e: SyntheticEvent) => {
      if (!token || !loanNumber) return;
      setOneTimePaymentModalOpen(false);
      setSubmitStatus('loading');
      setLoadingMessage('Saving...');
      let confirmationNos = [];
      let transactionResponse = null;
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      if (retainInfo) {
        try {
          const requestBody = {
            otpRoutingNo: autopayInfo.otpRoutingNo,
            otpBankAccountNo: autopayInfo.otpBankAccountNo.replace(/\s/g, ''),
            otpBankName: autopayInfo.otpBankName,
            otpBankAccountTypeKeyword: autopayInfo.otpBankAccountTypeKeyword,
            active: true,
          };
          const response = await axiosCall(
            { url: `live/loan/${loanNumber}/servicing`, method: 'post', data: requestBody },
            config,
            headers,
          );
          if (!response || response?.error) {
            throw Error('Unable to save otp info. ' + response?.error);
          }
        } catch (e) {
          console.error(e);
          setStatusAlert({
            status: 'error',
            message: `Failed to save otp info: ${e}`,
          });
        }
      }
      try {
        //get batch
        let batchNumber = '';
        const batchListResponse = await axiosCall(
          { url: 'live/servicing/payments/batch/list', method: 'get' },
          config,
          headers,
        );
        if (batchListResponse && batchListResponse.length > 0) {
          const filteredBatches = batchListResponse.filter(
            (batch) =>
              batch.active &&
              batch.postedSourceKeyword === 'PAY BY PHONE' &&
              dayjs.unix(batch.dateCreated).tz('America/Los_Angeles').isSame(dayjs().tz('America/Los_Angeles'), 'day'),
          );
          if (filteredBatches.length > 0) {
            batchNumber = filteredBatches[0].batchNo;
          }
        }
        //create new batch if one wasn't found
        if (!batchNumber) {
          const requestBody = {
            active: true,
            postedSourceKeyword: 'PAY BY PHONE',
          };
          let response = await axiosCall(
            { url: 'live/servicing/payments/batch/add', method: 'post', data: requestBody },
            config,
            headers,
          );
          if (response) {
            if (!response.batchNo) {
              throw Error('Unable to create batch.');
            } else {
              batchNumber = response.batchNo;
            }
          }
        }
        // suffix should be Pay by Phone (Z) if Servicer, Web Payment (H) if Borrower
        // exception is for transaction code 19, which should have suffix X
        const mcdTransSuffix = servicerUser ? 'Z' : 'H';
        if (otpInfo.processingFee > 0) {
          //create transaction 19X
          const createRequestBody = {
            active: true,
            dateReceived: otpInfo.processDate,
            accountType: otpBankAcctTypeQidInfo.find(
              (option) => option.keyword === autopayInfo.otpBankAccountTypeKeyword,
            ).code,
            accountNo: autopayInfo.otpBankAccountNo,
            routingNo: autopayInfo.otpRoutingNo,
            amount: otpInfo.processingFee,
            mcdTransType: '19',
            mcdTransSuffix: 'X',
            feeNo: 'S05',
          };
          let createResponse = await axiosCall(
            {
              url: `live/servicing/payments/batch/${batchNumber}/loan/${loanNumber}/add`,
              method: 'post',
              data: createRequestBody,
            },
            config,
            headers,
          );
          if (!createResponse || createResponse?.error) {
            throw Error('Unable to create process fee transaction. ' + createResponse?.error);
          } else {
            const cnt = createResponse?.cnt;
            const postRequestBody = {
              posted: true,
            };
            let postResponse = await axiosCall(
              { url: `live/servicing/payments/batch/${batchNumber}/cnt/${cnt}`, method: 'post', data: postRequestBody },
              config,
              headers,
            );
            if (!postResponse || postResponse?.error) {
              throw Error('Unable to post process fee transaction. ' + postResponse?.error);
            }
          }
        }
        let editedRequestFields = {};
        for (const property in otpInfo) {
          if (property !== 'processingFee' && property !== 'payment' && property !== 'processDate') {
            if (otpInfo[property] !== 0) {
              switch (property) {
                case 'additionalPrincipal':
                  editedRequestFields = {
                    ...editedRequestFields,
                    prinCurtailReqstd: otpInfo.additionalPrincipal,
                  };
                  break;
                case 'additionalEscrow':
                  editedRequestFields = {
                    ...editedRequestFields,
                    extraEscrowReqstd: otpInfo.additionalEscrow,
                  };
                  break;
                case 'lateCharges':
                  editedRequestFields = {
                    ...editedRequestFields,
                    lateChargeReqstd: otpInfo.lateCharges,
                  };
                  break;
                case 'corporateAdvance':
                  editedRequestFields = {
                    ...editedRequestFields,
                    corpAdvanceReqstd: otpInfo.corporateAdvance,
                  };
                  break;
                case 'assessments':
                  editedRequestFields = {
                    ...editedRequestFields,
                    assessmentsReqstd: otpInfo.assessments,
                  };
                  break;
                case 'suspense':
                  editedRequestFields = {
                    ...editedRequestFields,
                    suspenseReqstd: otpInfo.suspense,
                  };
                  break;
              }
            }
          }
        }
        //Monthly Payment
        let shouldCreateAssessmentTransaction = false;
        if (!removePayment) {
          let requestBody: { [key: string]: any } = {
            active: true,
            dateReceived: otpInfo.processDate,
            accountType: otpBankAcctTypeQidInfo.find(
              (option) => option.keyword === autopayInfo.otpBankAccountTypeKeyword,
            ).code,
            accountNo: autopayInfo.otpBankAccountNo,
            routingNo: autopayInfo.otpRoutingNo,
            amount: !shouldCreateAssessmentTransaction ? totalWithoutFee + otpInfo.processingFee : totalWithoutFee,
            mcdTransType: isCurrent ? '05' : '08',
            mcdTransSuffix: mcdTransSuffix,
          };
          for (const property in editedRequestFields) {
            requestBody = {
              ...requestBody,
              [property]: editedRequestFields[property],
            };
          }
          if (otpInfo.processingFee > 0) {
            if ('assessmentsReqstd' in editedRequestFields) {
              requestBody = {
                ...requestBody,
                assessmentsReqstd: editedRequestFields['assessmentsReqstd'] + otpInfo.processingFee,
              };
            } else {
              // SQC-313 - processing fee is already included in the monthly payment.
              requestBody = {
                ...requestBody,
                assessmentsReqstd: otpInfo.processingFee,
              };
            }
          }
          let response = await axiosCall(
            {
              url: `live/servicing/payments/batch/${batchNumber}/loan/${loanNumber}/add`,
              method: 'post',
              data: requestBody,
            },
            config,
            headers,
          );
          if (!response || response?.error) {
            throw Error('Unable to create monthly payments. ' + response?.error);
          }
          transactionResponse = response;
          // setConfirmationNumbers((prev) => [...prev, response.batchNo.toString() + '-' + response.cnt.toString()]);
          confirmationNos = [response.batchNo.toString() + '-' + response.cnt.toString()];
        } else {
          //individual transactions
          let batchRequestBody = {};
          let commonData = {
            active: true,
            dateReceived: otpInfo.processDate,
            accountType: otpBankAcctTypeQidInfo.find(
              (option) => option.keyword === autopayInfo.otpBankAccountTypeKeyword,
            ).code,
            accountNo: autopayInfo.otpBankAccountNo,
            routingNo: autopayInfo.otpRoutingNo,
          };
          let suspenseLeft = -1 * otpInfo.suspense;
          if (otpInfo.processingFee > 0 && !('assessmentsReqstd' in editedRequestFields)) {
            editedRequestFields = {
              ...editedRequestFields,
              assessmentsReqstd: 0,
            };
          }
          for (const property in editedRequestFields) {
            if (property === 'assessmentsReqstd' && otpInfo.processingFee > 0) {
              editedRequestFields[property] = editedRequestFields[property] + otpInfo.processingFee;
            }
            let addedSuspense = 0;
            if (otpInfo.suspense < 0) {
              if (property === 'suspenseReqstd') {
                continue;
              }
              if (suspenseLeft > 0) {
                if (suspenseLeft >= editedRequestFields[property]) {
                  addedSuspense = editedRequestFields[property];
                } else {
                  addedSuspense = suspenseLeft;
                }
                suspenseLeft -= addedSuspense;
              }
            }

            let transactionCode = '';
            switch (property) {
              case 'prinCurtailReqstd':
                transactionCode = '11';
                break;
              case 'extraEscrowReqstd':
                transactionCode = '13';
                break;
              case 'lateChargeReqstd':
                transactionCode = '14';
                break;
              case 'corpAdvanceReqstd':
                transactionCode = '26';
                break;
              case 'assessmentsReqstd':
                transactionCode = '20';
                break;
              case 'suspenseReqstd':
                transactionCode = '16';
                break;
            }
            batchRequestBody = {
              ...batchRequestBody,
              [property]: {
                endpoint: `/servicing/payments/batch/${batchNumber}/loan/${loanNumber}/add`,
                data: {
                  ...commonData,
                  [property]: editedRequestFields[property],
                  suspenseReqstd: property === 'suspenseReqstd' ? editedRequestFields[property] : -1 * addedSuspense,
                  amount: editedRequestFields[property] + -1 * addedSuspense,
                  mcdTransType: transactionCode,
                  mcdTransSuffix: mcdTransSuffix,
                },
              },
            };
          }
          let response: Record<string, batchTransactionResponseType> = await axiosCall(
            { url: `/batch`, method: 'post', data: batchRequestBody },
            config,
            headers,
          );
          if (!response || response?.error) {
            throw Error('Unable to create payments. ' + response?.error);
          }
          // setConfirmationNumbers(
          //   Object.values(response).map(
          //     (res: batchTransactionResponseType) => res.data.batchNo.toString() + '-' + res.data.cnt.toString(),
          //   ),
          // );
          // we only send the first transaction confirmation number in the email
          const firstTransaction = Object.values(response)[0];
          transactionResponse = firstTransaction.data;
          confirmationNos = Object.values(response).map(
            (res: batchTransactionResponseType) => res.data.batchNo.toString() + '-' + res.data.cnt.toString(),
          );
        }
        if (shouldCreateAssessmentTransaction) {
          let requestBody: { [key: string]: any } = {
            active: true,
            dateReceived: otpInfo.processDate,
            accountType: otpBankAcctTypeQidInfo.find(
              (option) => option.keyword === autopayInfo.otpBankAccountTypeKeyword,
            ).code,
            accountNo: autopayInfo.otpBankAccountNo,
            routingNo: autopayInfo.otpRoutingNo,
            amount: otpInfo.processingFee,
            mcdTransType: '20',
            mcdTransSuffix: mcdTransSuffix,
            assessmentsReqstd: otpInfo.processingFee,
          };
          let response = await axiosCall(
            {
              url: `live/servicing/payments/batch/${batchNumber}/loan/${loanNumber}/add`,
              method: 'post',
              data: requestBody,
            },
            config,
            headers,
          );
          if (!response || response?.error) {
            throw Error('Unable to create assessment transaction for process fee. ' + response?.error);
          }
        }
        setSubmitStatus('done');
        setLoadingMessage('');
        setStatusAlert({
          status: 'success',
          message: 'Payment Submitted! An email containing the confirmation number has been sent.',
        });
        refetch();
        await handleConfirmationEmail(transactionResponse);
        await handleAddComment(e, confirmationNos);
      } catch (e) {
        setSubmitStatus('done');
        setLoadingMessage('');
        console.error(e);
        setStatusAlert({
          status: 'error',
          message: `Failed to submit payment: ${e}`,
        });
      }
    },
    [
      autopayInfo,
      otpInfo,
      loanNumber,
      retainInfo,
      isCurrent,
      otpBankAcctTypeQidInfo,
      removePayment,
      servicerUser,
      totalWithoutFee,
      token,
      handleAddComment,
      get,
    ],
  );

  // Set the bank account fields to blank
  const handleRemoveBankDetails = useCallback(async () => {
    if (!token || !loanNumber) return;
    setSubmitStatus('loading');

    try {
      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const requestBody = {
        otpRoutingNo: '',
        otpBankAccountNo: '',
        otpBankAccountTypeKeyword: '',
        otpBankName: '',
        active: true,
      };
      const response = await axiosCall(
        { url: `live/loan/${loanNumber}/servicing`, method: 'post', data: requestBody },
        config,
        headers,
      );
      if (!response || response?.error) {
        throw Error('Unable to save bank details. ' + response?.error);
      }
    } catch (e) {
      console.error(e);
      setStatusAlert({
        status: 'error',
        message: `Failed to save bank details: ${e}`,
      });
    }
    setSubmitStatus('done');
    await refetchAutopayInfo();
    setOneTimePaymentModalOpen(false);
    setOriginalBankingDetails(null);
  }, [loanNumber, token]);

  return (
    <OneTimePaymentContext.Provider
      value={{
        autopayInfo,
        dispatchAutopayInfo,
        paymentInfo,
        otpInfo,
        dispatchOtpInfo,
        handleSubmit,
        handleRemoveTransaction,
        handleSaveTransaction,
        status,
        statusAlert,
        setStatusAlert,
        otpBankAcctTypeQidInfo,
        otpRoutingNoError,
        setOtpRoutingNoError,
        otpBankNameError,
        setOtpBankNameError,
        otpBankAccountNoError,
        setOtpBankAccountNoError,
        otpBankAccountTypeError,
        setOtpBankAccountTypeError,
        additionalPrincipalError,
        setAdditionalPrincipalError,
        additionalEscrowError,
        setAdditionalEscrowError,
        lateChargesError,
        setLateChargesError,
        lateChargesMaxError,
        setLateChargesMaxError,
        corporateAdvanceError,
        setCorporateAdvanceError,
        corporateAdvanceMaxError,
        setCorporateAdvanceMaxError,
        assessmentsError,
        setAssessmentsError,
        assessmentsMaxError,
        setAssessmentsMaxError,
        suspenseError,
        setSuspenseError,
        suspenseMaxError,
        setSuspenseMaxError,
        processingFeeError,
        setProcessingFeeError,
        processDateError,
        setProcessDateError,
        processTomorrowError,
        setProcessTomorrowError,
        paymentError,
        setPaymentError,
        totalError,
        setTotalError,
        retainInfo,
        setRetainInfo,
        removePayment,
        setRemovePayment,
        tempPayment,
        setTempPayment,
        removeProcessingFee,
        setRemoveProcessingFee,
        oneTimePaymentModalOpen,
        setOneTimePaymentModalOpen,
        oneTimePaymentModalContent,
        setOneTimePaymentModalContent,
        deleteRow,
        setDeleteRow,
        validateFields,
        isValidatingPayment,
        loadingMessage,
        isCurrent,
        currentMonthDueWithoutPayment,
        escrowShortageModalOpen,
        setEscrowShortageModalOpen,
        handleMonthlyInstallments,
        handleLumpSum,
        choseLumpSum,
        pageError,
        futurePayments,
        hasFuturePayment,
        hasAutopay,
        stopCodeST01,
        stopCodeST02,
        stopCodeST03,
        stopCodeST04,
        stopCodeST20,
        hasDisablingStopCode,
        handleRemoveStopCode,
        statusForeclosure,
        statusDeedInLieu,
        statusShortSale,
        statusServicingTransfer,
        hasDisablingStatus,
        minProcessDate,
        totalWithoutFee,
        loanBalancesInfo,
        routingError,
        routingErrorMessage,
        serviceManager,
        handleRemoveBankDetails,
        originalBankingDetails,
      }}
    >
      {children}
    </OneTimePaymentContext.Provider>
  );
};

export { OneTimePaymentProvider, useOneTimePaymentContext };
